import type { ExecutionActivityViewModel, ExecutionTripViewModel } from './executionTypes';

export interface ExecutionTimelinePoint {
  activity: ExecutionActivityViewModel;
  plannedPercent: number | null;
  actualPercent: number | null;
  plannedServiceStartPercent: number | null;
  plannedServiceWidthPercent: number;
  actualServiceStartPercent: number | null;
  actualServiceWidthPercent: number;
  actualTravelStartPercent: number | null;
  actualTravelWidthPercent: number;
  next: boolean;
}

export interface ExecutionTimelineModel {
  startEpoch: number;
  endEpoch: number;
  contentWidth: number;
  currentPercent: number | null;
  points: ExecutionTimelinePoint[];
  ticks: Array<{ epoch: number; percent: number }>;
}

const clamp = (value: number): number => Math.min(100, Math.max(0, value));

const getLocalMinuteOfDay = (epoch: number, timezone: string): number => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(epoch));
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return (Number(values.hour) * 60) + Number(values.minute);
  } catch {
    const date = new Date(epoch);
    return (date.getUTCHours() * 60) + date.getUTCMinutes();
  }
};

export const buildExecutionTimelineModel = (trip: ExecutionTripViewModel): ExecutionTimelineModel => {
  const epochs = trip.activities.flatMap((activity) => [
    activity.plannedArrivalAtEpoch,
    activity.plannedDepartureAtEpoch,
    activity.actualCheckInAtEpoch,
    activity.actualCheckOutAtEpoch,
  ]).filter((epoch): epoch is number => epoch != null && Number.isFinite(epoch));
  const rawStart = Math.min(trip.plannedTrip.plannedDepartureAtEpoch, ...epochs);
  const rawEnd = Math.max(trip.plannedTrip.plannedReturnAtEpoch, trip.updatedAtEpoch, ...epochs);
  const padding = Math.max(10 * 60_000, (rawEnd - rawStart) * 0.035);
  const startEpoch = rawStart - padding;
  const endEpoch = Math.max(rawEnd + padding, startEpoch + 60 * 60_000);
  const span = endEpoch - startEpoch;
  const percent = (epoch: number | null): number | null => epoch == null ? null : clamp(((epoch - startEpoch) / span) * 100);
  const width = (start: number | null, end: number | null): number => (
    start == null || end == null ? 0 : Math.max(0.7, ((Math.max(start, end) - start) / span) * 100)
  );
  const nextActivity = trip.activities.find((activity) => activity.kind === 'TOUCH_POINT' && activity.outcome === 'PENDING');

  const points = trip.activities.map((activity, index) => {
    const previous = trip.activities[index - 1];
    const actualArrival = activity.actualCheckInAtEpoch;
    const actualDeparture = activity.actualCheckOutAtEpoch ?? (
      activity.outcome === 'IN_PROGRESS' ? trip.updatedAtEpoch : activity.actualCheckInAtEpoch
    );
    const actualTravelStart = actualArrival == null
      ? null
      : previous?.actualCheckOutAtEpoch ?? previous?.actualCheckInAtEpoch ?? null;
    return {
      activity,
      plannedPercent: percent(activity.plannedArrivalAtEpoch),
      actualPercent: percent(actualArrival),
      plannedServiceStartPercent: percent(activity.plannedArrivalAtEpoch),
      plannedServiceWidthPercent: width(activity.plannedArrivalAtEpoch, activity.plannedDepartureAtEpoch),
      actualServiceStartPercent: percent(actualArrival),
      actualServiceWidthPercent: width(actualArrival, actualDeparture),
      actualTravelStartPercent: percent(actualTravelStart),
      actualTravelWidthPercent: width(actualTravelStart, actualArrival),
      next: activity.id === nextActivity?.id,
    };
  });

  const spanMinutes = span / 60_000;
  const tickStepMinutes = spanMinutes <= 180 ? 30 : spanMinutes <= 540 ? 60 : 120;
  const tickStep = tickStepMinutes * 60_000;
  const startLocalMinute = getLocalMinuteOfDay(startEpoch, trip.plannedTrip.timezone);
  const minutesToFirstTick = (tickStepMinutes - (startLocalMinute % tickStepMinutes)) % tickStepMinutes;
  const firstTick = startEpoch + (minutesToFirstTick * 60_000);
  const ticks: Array<{ epoch: number; percent: number }> = [];
  for (let epoch = firstTick; epoch <= endEpoch; epoch += tickStep) {
    ticks.push({ epoch, percent: clamp(((epoch - startEpoch) / span) * 100) });
  }

  return {
    startEpoch,
    endEpoch,
    contentWidth: Math.min(1600, Math.max(760, Math.round(spanMinutes * 3.2))),
    currentPercent: trip.phase === 'COMPLETED' ? null : percent(trip.updatedAtEpoch),
    points,
    ticks,
  };
};
